import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import "./StudentInsights.css";

// Palette pulled from the app tokens so the charts match the rest of the UI.
const C = {
  primary: "#59738d",
  info: "#2563eb",
  success: "#16a34a",
  warning: "#b45309",
  grid: "#eef0f3",
};

const isBathroom = (loc = "") => /bathroom|restroom|toilet|washroom/i.test(loc);

function durationMins(rec) {
  if (!rec.time_in || !rec.time_out) return null;
  const ms = new Date(rec.time_in).getTime() - new Date(rec.time_out).getTime();
  return ms > 0 ? Math.round(ms / 60000) : null;
}

// Compact dark tooltip — the recharts default is oversized and off-palette.
const Tip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="insights-tip">
      <div className="insights-tip-label">{label}</div>
      <div className="insights-tip-val">{payload[0].value}{unit}</div>
    </div>
  );
};

const axisTick = { fontSize: 11, fill: "#8a8d9b" };
const barMargin = { top: 8, right: 12, left: -20, bottom: 0 };

function ChartCard({ item, onOpen }) {
  return (
    <div
      className={`insights-chart-card${item.wide ? " is-wide" : ""}${item.empty ? "" : " is-clickable"}`}
      onClick={item.empty ? undefined : () => onOpen(item)}
    >
      <div className="insights-chart-head">
        <h3>{item.title}</h3>
        <div className="insights-chart-meta">
          {item.subtitle && <span>{item.subtitle}</span>}
          {!item.empty && <span className="insights-expand" title="Click to enlarge">⤢</span>}
        </div>
      </div>
      {item.empty ? (
        <div className="insights-chart-empty">Not enough data yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={item.wide ? 220 : 190}>
          {item.render()}
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function StudentInsightsCharts({ records = [] }) {
  const [zoom, setZoom] = useState(null);

  // Daily trip trend — the clearest signal of an escalating (or fading) pattern.
  const byDate = useMemo(() => {
    const m = {};
    records.forEach((r) => {
      if (!r.time_out) return;
      const key = new Date(r.time_out).toISOString().slice(0, 10);
      m[key] = (m[key] || 0) + 1;
    });
    return Object.keys(m)
      .sort()
      .map((date) => ({
        label: new Date(date + "T00:00:00").toLocaleDateString([], { month: "numeric", day: "numeric" }),
        count: m[date],
      }));
  }, [records]);

  // By hour of the day, filled across the active range so gaps read as zero
  // (not as missing bars crammed together like the old chart).
  const byHour = useMemo(() => {
    const m = {};
    records.forEach((r) => {
      if (!r.time_out) return;
      const h = new Date(r.time_out).getHours();
      m[h] = (m[h] || 0) + 1;
    });
    const hrs = Object.keys(m).map(Number);
    if (!hrs.length) return [];
    const lo = Math.min(...hrs);
    const hi = Math.max(...hrs);
    const out = [];
    for (let h = lo; h <= hi; h++) {
      out.push({ label: `${(h % 12) || 12}${h >= 12 ? "p" : "a"}`, count: m[h] || 0 });
    }
    return out;
  }, [records]);

  // By weekday (Mon–Fri, zeros filled).
  const byDay = useMemo(() => {
    const idx = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri" };
    const m = {};
    records.forEach((r) => {
      if (!r.time_out) return;
      const name = idx[new Date(r.time_out).getDay()];
      if (name) m[name] = (m[name] || 0) + 1;
    });
    return ["Mon", "Tue", "Wed", "Thu", "Fri"].map((day) => ({ day, count: m[day] || 0 }));
  }, [records]);

  // Top destinations (bathroom highlighted).
  const byLocation = useMemo(() => {
    const m = {};
    records.forEach((r) => {
      const l = r.location || "Unknown";
      m[l] = (m[l] || 0) + 1;
    });
    return Object.entries(m)
      .map(([location, count]) => ({ location, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [records]);

  // Trip-length buckets — flags the long absences worth a second look.
  const byDuration = useMemo(() => {
    const buckets = [
      { label: "<3m", min: 0, max: 3, count: 0 },
      { label: "3–5m", min: 3, max: 5, count: 0 },
      { label: "5–10m", min: 5, max: 10, count: 0 },
      { label: "10m+", min: 10, max: Infinity, count: 0 },
    ];
    let any = false;
    records.forEach((r) => {
      const m = durationMins(r);
      if (m == null) return;
      any = true;
      const b = buckets.find((b) => m >= b.min && m < b.max);
      if (b) b.count += 1;
    });
    return any ? buckets.map(({ label, count }) => ({ label, count })) : [];
  }, [records]);

  const items = [
    {
      id: "trend",
      title: "Trips over time",
      subtitle: "by day",
      wide: true,
      empty: byDate.length < 2,
      render: () => (
        <AreaChart data={byDate} margin={barMargin}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} minTickGap={16} />
          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={28} />
          <Tooltip content={<Tip unit=" trips" />} cursor={{ stroke: C.info, strokeOpacity: 0.2 }} />
          <Area type="monotone" dataKey="count" stroke={C.info} strokeWidth={2} fill={C.info} fillOpacity={0.12} />
        </AreaChart>
      ),
    },
    {
      id: "hour",
      title: "Time of day",
      subtitle: "when they leave",
      empty: !byHour.length,
      render: () => (
        <BarChart data={byHour} margin={barMargin}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={28} />
          <Tooltip content={<Tip unit=" trips" />} cursor={{ fill: "#00000008" }} />
          <Bar dataKey="count" fill={C.primary} radius={[4, 4, 0, 0]} maxBarSize={44} />
        </BarChart>
      ),
    },
    {
      id: "day",
      title: "Day of week",
      empty: !records.length,
      render: () => (
        <BarChart data={byDay} margin={barMargin}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="day" tick={axisTick} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={28} />
          <Tooltip content={<Tip unit=" trips" />} cursor={{ fill: "#00000008" }} />
          <Bar dataKey="count" fill={C.success} radius={[4, 4, 0, 0]} maxBarSize={44} />
        </BarChart>
      ),
    },
    {
      id: "location",
      title: "Where they go",
      subtitle: "top destinations",
      empty: !byLocation.length,
      render: () => (
        <BarChart data={byLocation} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="location" tick={axisTick} tickLine={false} axisLine={false} width={88} />
          <Tooltip content={<Tip unit=" trips" />} cursor={{ fill: "#00000008" }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
            {byLocation.map((e, i) => (
              <Cell key={i} fill={isBathroom(e.location) ? C.warning : C.primary} />
            ))}
          </Bar>
        </BarChart>
      ),
    },
    {
      id: "duration",
      title: "Trip length",
      subtitle: "completed trips",
      empty: !byDuration.length,
      render: () => (
        <BarChart data={byDuration} margin={barMargin}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={28} />
          <Tooltip content={<Tip unit=" trips" />} cursor={{ fill: "#00000008" }} />
          <Bar dataKey="count" fill={C.info} radius={[4, 4, 0, 0]} maxBarSize={44} />
        </BarChart>
      ),
    },
  ];

  return (
    <>
      <div className="insights-charts">
        {items.map((item) => (
          <ChartCard key={item.id} item={item} onOpen={setZoom} />
        ))}
      </div>

      {zoom && (
        <div className="insights-modal-overlay" onClick={() => setZoom(null)}>
          <div className="insights-modal" onClick={(e) => e.stopPropagation()}>
            <div className="insights-modal-head">
              <h3>
                {zoom.title}
                {zoom.subtitle && <span> · {zoom.subtitle}</span>}
              </h3>
              <button className="insights-modal-close" onClick={() => setZoom(null)} aria-label="Close">×</button>
            </div>
            <ResponsiveContainer width="100%" height={440}>
              {zoom.render()}
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </>
  );
}
